import { Office } from "@/type/core/offices/offices.types";
import { TransactionDetail } from "@/type/core/transaction/transaction.types";
import { TransactionHelper } from "@/utils/helpers/transaction-helper";
import DashboardFilters from "@components/core/filters/DashboardFilters";
import InfoModal from "@components/core/modals/InfoModal";
import Shortcuts from "@components/core/shortcuts/Shortcuts";
import DashboardTable from "@components/core/tables/DashboardTable";
import { authenticationStore } from "@state/auth/AuthStore";
import { ViewDetailResponseDto } from "@state/transactions/response/view.detail.response.dto";
import {
  requestSearchTransactions,
  requestSearchViews,
} from "@state/transactions/TransactionEffects";
import { AuthenticationContext } from "@type/core/auth/auth.types";
import { Filters } from "@type/core/dashboard/dashboard.types";
import { Pageable } from "@type/core/pagination/pagination.types";
import { emptySearchResult, initialFilters } from "@utils/Constant";
import { toastError } from "@utils/helpers/toast-helper";
import { useUnit } from "effector-react";
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

interface DashboardSectionProps {
  className?: string;
}

const DashboardSection: FunctionComponent<DashboardSectionProps> = ({
  className = "",
}) => {
  const { t } = useTranslation();

  const authStore = useUnit<AuthenticationContext | null>(authenticationStore);

  const [views, setViews] = useState<ViewDetailResponseDto[]>([]);
  const [selectedFilters, setSelectedFilters] =
    useState<Filters>(initialFilters);
  const [selectedShortcut, setSelectedShortcut] =
    useState<string>("myDashboard");

  const [transactionsLoading, setTransactionsLoading] = useState<boolean>(true);
  const [transactions, setTransactions] =
    useState<Pageable<TransactionDetail>>(emptySearchResult);

  const [pageNumber, setPageNumber] = useState<number>(1);
  const [limit, setLimit] = useState<number>(20);

  useEffect(() => {
    setSelectedFilters(initialFilters);
  }, [selectedShortcut]);

  const cabinet = useMemo<Office[]>(() => {
    return (
      authStore?.user?.offices.filter((office) =>
        authStore?.selectedOfficeIds?.includes(office.id),
      ) ?? []
    );
  }, [authStore?.user?.offices, authStore?.selectedOfficeIds]);

  const body = useMemo(() => {
    return TransactionHelper.buildBodyGetTransaction(
      {
        ...selectedFilters,
        cabinet,
      },
      selectedFilters.search,
      { current: pageNumber, size: limit },
      { order: 1, type: "default" },
      selectedShortcut,
    );
  }, [selectedFilters, cabinet, pageNumber, limit, selectedShortcut]);

  const fetchTransactions = useCallback(() => {
    setTransactionsLoading(true);
    requestSearchTransactions({ dto: body })
      .catch(() => {
        toastError(t("error.dashboard.search"));
      })
      .finally(() => {
        setTransactionsLoading(false);
      });
  }, [t, body]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  useEffect(() => {
    return requestSearchTransactions.done.watch(({ result }) => {
      if (result.ok && result.data) {
        setTransactions(result.data);
      } else {
        toastError(t("error.dashboard.search"));
      }
    });
  });

  useEffect(() => {
    void requestSearchViews({
      dto: { officeIds: authStore?.selectedOfficeIds || [] },
    });
  }, [fetchTransactions, authStore?.selectedOfficeIds]);

  useEffect(() => {
    return requestSearchViews.done.watch(({ result }) => {
      if (result.ok && result.data) {
        setViews(result.data.views || []);
      } else {
        toastError(t("error.dashboard.search"));
      }
    });
  });

  return (
    <div className={`app-section-dashboard ${className}`}>
      <h4 className="pb-4 ml-8" data-test={"title"}>
        {t("home.dashboard.shortcuts.title")}
        <InfoModal value="shortcut" modalClassName="text-justify" />
      </h4>
      <Shortcuts
        views={views}
        selectedShortcut={selectedShortcut}
        setSelectedShortcut={setSelectedShortcut}
      />
      <h4 className="pb-4 ml-8">{t("home.dashboard.filters.title")}</h4>
      <DashboardFilters
        selectedFilters={selectedFilters}
        setSelectedFilters={setSelectedFilters}
      />
      <DashboardTable
        transactionsLoading={transactionsLoading}
        refetchTransactions={() => fetchTransactions()}
        sortedTransactions={transactions}
        setLimit={setLimit}
        setPageNumber={setPageNumber}
        body={body}
      />
    </div>
  );
};

export default DashboardSection;
